import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { SelectFormInput } from "src/components/form";
import { TrialInstitutionItemsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialInstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionItemsPageActions from "../../../actions/pages/trialInstitutionItemsPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialInstitutionItemActions from "../../../actions/trialInstitutionItem";
import * as TrialInstitutionItemCategoryActions from "../../../actions/trialInstitutionItemCategory";
import * as TrialInstitutionItemTemplateActions from "../../../actions/trialInstitutionItemTemplate";
import { InlineLoading, Modal, NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import CollectionInstanceComponent from "../../../containers/Collection/CollectionInstance/CollectionInstanceComponent";
import { TrialInstitutionItemCreateVersion } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { setModalTitle } from "../../../helpers/modalHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialInstitutionItemsPage.scss";

interface TrialInstitutionItemsPageProps {
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    collectionInstanceModalOpen: boolean;
    collectionInstanceId: number;
    modalTitle: string;
    modalDescription: string;
    trialInstitutionItemTemplates: Dtos.TrialInstitutionItemTemplate[];
    loadingTrialInstitutionItemTemplates: boolean;
    loadTrialInstitutionItemTemplatesSuccess: boolean;
    loadTrialInstitutionItemTemplatesFailure: boolean;
    loadTrialInstitutionItemTemplatesErrorCode: string;
    trialInstitutionItemCategories: Dtos.TrialInstitutionItemCategory[];
    loadingTrialInstitutionItemCategories: boolean;
    loadTrialInstitutionItemCategoriesSuccess: boolean;
    loadTrialInstitutionItemCategoriesFailure: boolean;
    loadTrialInstitutionItemCategoriesErrorCode: string;
    savingTrialInstitutionItem: boolean;
    saveTrialInstitutionItemSuccess: boolean;
    createVersionModalOpen: boolean;
    createVersionTrialInstitutionItem: Dtos.TrialInstitutionItem;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setEditModelViewState: TrialInstitutionItemsPageActions.ITrialInstitutionItemsPageSetEditModalViewStateActionCreator;
    setCreateVersionModelViewState: TrialInstitutionItemsPageActions.ITrialInstitutionItemsPageSetCreateVersionModalViewStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialInstitutionItemTemplates: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateClearActionCreator;
    loadTrialInstitutionItemTemplates: TrialInstitutionItemTemplateActions.ITrialInstitutionItemTemplateLoadActionCreator;
    clearTrialInstitutionItemCategories: TrialInstitutionItemCategoryActions.ITrialInstitutionItemCategoriesClearActionCreator;
    loadTrialInstitutionItemCategories: TrialInstitutionItemCategoryActions.ITrialInstitutionItemCategoriesLoadByTrialInstitutionIdActionCreator;
    saveTrialInstitutionItem: TrialInstitutionItemActions.ITrialInstitutionItemSaveActionCreator;
}

const TrialInstitutionItemsPage: FC<TrialInstitutionItemsPageProps> = ({
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    collectionInstanceModalOpen,
    collectionInstanceId,
    modalTitle,
    modalDescription,
    trialInstitutionItemTemplates,
    loadingTrialInstitutionItemTemplates,
    loadTrialInstitutionItemTemplatesSuccess,
    loadTrialInstitutionItemTemplatesFailure,
    loadTrialInstitutionItemTemplatesErrorCode,
    trialInstitutionItemCategories,
    loadingTrialInstitutionItemCategories,
    loadTrialInstitutionItemCategoriesSuccess,
    loadTrialInstitutionItemCategoriesFailure,
    loadTrialInstitutionItemCategoriesErrorCode,
    savingTrialInstitutionItem,
    saveTrialInstitutionItemSuccess,
    createVersionModalOpen,
    createVersionTrialInstitutionItem,
    clearTrialInstitution,
    loadTrialInstitution,
    setEditModelViewState,
    setCreateVersionModelViewState,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialInstitutionItemTemplates,
    loadTrialInstitutionItemTemplates,
    saveTrialInstitutionItem,
    clearTrialInstitutionItemCategories,
    loadTrialInstitutionItemCategories
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- States --
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [addTrialInstitutionItemId, setAddTrialInstitutionItemId] = useState<number | undefined>(undefined);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] = useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialInstitutionItems =
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess &&
        !loadingTrialInstitutionItemCategories &&
        loadTrialInstitutionItemCategoriesSuccess &&
        !creatingTree;


    // Load Trial Institution & Trial Institution Items
    useEffect(() => {
        if (
            trialIdNumber &&
            trialInstitutionIdNumber &&
            !loadingTrialInstitution &&
            !trialInstitution
        ) {
            setBreadcrumbs(
                TrialInstitutionItemsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialInstitutionItemCategories(trialInstitutionIdNumber);
        }

        if (
            trialInstitution &&
            !loadingTrialInstitutionItemTemplates &&
            !loadTrialInstitutionItemTemplatesSuccess
        ) {
            loadTrialInstitutionItemTemplates(trialInstitution?.trialId);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        trialInstitutionItemTemplates,
        loadingTrialInstitution,
        loadingTrialInstitutionItemTemplates,
        setBreadcrumbs,
        TrialInstitutionItemsBreadcrumbs,
        loadTrialInstitution,
        loadTrialInstitutionItemTemplates,
        loadTrialInstitutionItemTemplatesSuccess,
        loadTrialInstitutionItemCategories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadTrialInstitutionItemTemplatesFailure) {
            navigate(`/error/${loadTrialInstitutionItemTemplatesErrorCode}`);
        }
        if (loadTrialInstitutionItemCategoriesFailure) {
            navigate(`/error/${loadTrialInstitutionItemCategoriesErrorCode}`);
        }
    }, [
        loadTrialInstitutionFailure,
        loadTrialInstitutionItemTemplatesFailure,
        loadTrialInstitutionErrorCode,
        loadTrialInstitutionItemTemplatesErrorCode,
        loadTrialInstitutionItemCategoriesFailure,
        loadTrialInstitutionItemCategoriesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs & side menu
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(
                TrialInstitutionSubMenu(trialInstitution, permissions)
            );
            setBreadcrumbs(
                TrialInstitutionItemsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialInstitutionSubMenu,
        TrialInstitutionItemsBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial institution & item on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitution();
            clearTrialInstitutionItemTemplates();
            clearTrialInstitutionItemCategories();
        };
    }, [clearTrialInstitution, clearTrialInstitutionItemTemplates, clearTrialInstitutionItemCategories]);

    // Reload trial institution & items after saving trial institution item
    useEffect(() => {
        if (!savingTrialInstitutionItem && saveTrialInstitutionItemSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial institution item saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialInstitutionItemCategories(trialInstitutionIdNumber);
        }
    }, [
        savingTrialInstitutionItem,
        saveTrialInstitutionItemSuccess,
        trialInstitutionIdNumber,
        loadTrialInstitution,
        loadTrialInstitutionItemCategories
    ]);

    // Refresh tree
    useEffect(() => {
        if (trialInstitutionItemCategories) {
            setInitalizedTreeData(false);
        }
    }, [trialInstitutionItemCategories, setInitalizedTreeData]);

    const handleModalClose = () => {
        setIsOpen(false);
        setAddTrialInstitutionItemId(undefined);
    };

    const handleModalSubmit = async () => {
        if (!addTrialInstitutionItemId || !trialInstitutionIdNumber) {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the trial institution item</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
            return;
        }
        let template = trialInstitutionItemTemplates.find((x) => x.id === addTrialInstitutionItemId);
        let trialInstitutionItem: Dtos.TrialInstitutionItem = {
            trialInstitutionId: trialInstitutionIdNumber,
            trialInstitutionItemTemplateId: addTrialInstitutionItemId,
            version: template?.initialVersion ?? null,
            active: true,
        } as Dtos.TrialInstitutionItem;
        await saveTrialInstitutionItem(trialInstitutionItem, null);

        setIsOpen(false);
        setAddTrialInstitutionItemId(undefined);
    };

    // Create tree
    useEffect(() => {
        if (trialInstitutionItemCategories && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial institution items return
            if (trialInstitutionItemCategories?.length == 0) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (
                trialInstitutionItemSummaries: Dtos.TrialInstitutionItemSummary[]
            ) => {
                let children = [];
                const groupedSummaries =
                    Object.values<Dtos.TrialInstitutionItemSummary>(
                        trialInstitutionItemSummaries.reduce((acc, item) => {
                            const key = `${item.trialInstitutionId}-${item.trialInstitutionItemTemplateId}`;

                            if (!acc[key] || new Date(item.dateEntered) > new Date(acc[key].dateEntered)) {
                                acc[key] = item;
                            }

                            return acc;
                        }, {})
                    );

                children =
                    groupedSummaries
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "1fr 3fr 3fr 3fr 2fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.FILE} />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.version && (
                                            <button
                                                disabled={
                                                    !AuthenticationHelper.hasPermission(
                                                        permissions,
                                                        Dtos.Permission.TrialInstitutionItemUpdate,
                                                        null,
                                                        null
                                                    )
                                                }
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm btn-row-version"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (y?.collectionInstanceId) {
                                                        setEditModelViewState(
                                                            true,
                                                            y.collectionInstanceId,
                                                            y?.name,
                                                            y?.description
                                                        );
                                                    }
                                                }}
                                            >
                                                <span>{y.version}</span>
                                            </button>
                                        )}
                                        {y?.trialInstitutionItemSummaryVersions?.map(
                                            (item, index) => (
                                                <button
                                                    key={`version-button-${item?.version}-${index}`}
                                                    className="btn btn-secondary btn-sm btn-row-version"
                                                    type="button"
                                                    disabled={
                                                        !AuthenticationHelper.hasPermission(
                                                            permissions,
                                                            Dtos.Permission.TrialInstitutionItemUpdate,
                                                            null,
                                                            null
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (item?.collectionInstanceId) {
                                                            setEditModelViewState(
                                                                true,
                                                                item.collectionInstanceId,
                                                                y?.name,
                                                                y?.description
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <span>{item.version}</span>
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="px-1 d-flex align-items-start justify-content-end">
                                        {y?.allowVersions && (
                                            <button
                                                disabled={false}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const trialInstitutionItems = trialInstitutionItemCategories?.flatMap(x => x.trialInstitutionItems) ?? [];
                                                    setCreateVersionModelViewState(
                                                        true,
                                                        trialInstitutionItems.find((x) => x.id == y.id)
                                                    );
                                                }}
                                                type="button"
                                                className="btn btn-outline-secondary mr-2 btn-sm"
                                            >
                                                <span>New version</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 3fr 3fr 2fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Version</strong>
                                </div>
                                <div className="px-1"></div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                [...trialInstitutionItemCategories]
                    ?.sort((a, b) => a?.trialInstitutionItemTemplateCategory?.rank - b?.trialInstitutionItemTemplateCategory?.rank)
                    ?.map((x) => ({
                        id: `category-${x?.trialInstitutionItemTemplateCategory?.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.trialInstitutionItemTemplateCategory?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        { [...new Set(x?.trialInstitutionItems?.map((x) => x.trialInstitutionItemTemplateId)),]?.length }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.trialInstitutionItemSummaries),],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialInstitutionItemCategories,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                } else if (id.includes("item")) {
                    const trialInstitutionItems = trialInstitutionItemCategories?.flatMap(x => x.trialInstitutionItems) ?? [];
                    const selectedTrialInsitutionItem: Dtos.TrialInstitutionItem = trialInstitutionItems?.find((x) => x.id?.toString() == id.replace("item-", ""));
                    setEditModelViewState(
                        true,
                        selectedTrialInsitutionItem?.collectionInstanceId,
                        selectedTrialInsitutionItem?.trialInstitutionItemTemplate?.name,
                        selectedTrialInsitutionItem?.trialInstitutionItemTemplate?.description
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions, trialInstitutionItemCategories]
    );

    const renderCreateVersionModal = useMemo(() => {
        if (trialInstitution && !loadingTrialInstitution) {
            return (
                <Modal
                    open={createVersionModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setCreateVersionModelViewState(false, null);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {setModalTitle(undefined, "Create Version")}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div
                        className="modal-body"
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                        <TrialInstitutionItemCreateVersion
                            trialInstitutionId={trialInstitutionIdNumber}
                            currentTrialInstitutionItem={createVersionTrialInstitutionItem}
                            onClose={() => {
                                setCreateVersionModelViewState(false, null);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution,
        createVersionModalOpen,
        setCreateVersionModelViewState,
        loadTrialInstitution,
        setModalTitle,
        createVersionTrialInstitutionItem,
    ]);

    const renderItemModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [Dtos.Permission.TrialInstitutionItemUpdate]
        );
        const formDisabled = !hasPermission;

        if (collectionInstanceId && collectionInstanceId > 0) {
            return (
                <Modal
                    open={collectionInstanceModalOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setEditModelViewState(false, null, "", "");
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h5 className="mb-0">
                                {setModalTitle(undefined, modalTitle)}
                            </h5>
                            {modalDescription ? (
                                <small>
                                    {modalDescription}
                                </small>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body customscrollbar">
                        <CollectionInstanceComponent 
                            collectionInstanceId={collectionInstanceId}
                            readonly={formDisabled}
                            onClose={() => {
                                setEditModelViewState(false, null, "", "");
                            }}
                            onSaved={() => {
                                loadTrialInstitutionItemCategories(trialInstitutionIdNumber);
                            }}
                        />
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        collectionInstanceId,
        collectionInstanceModalOpen,
        permissions,
        modalTitle,
        modalDescription,
        trialInstitutionIdNumber,
        setEditModelViewState,
        loadTrialInstitutionItemCategories,
    ]);

    const selectOptions = useMemo(() => {
        if (!trialInstitutionItemTemplates || !trialInstitutionItemCategories) return [];

        const trialInstitutionItems = trialInstitutionItemCategories?.flatMap(x => x.trialInstitutionItems) ?? [];
        const currentIds = [...new Set(trialInstitutionItems?.map((x) => x.trialInstitutionItemTemplateId))];

        var options = trialInstitutionItemTemplates
            ?.filter((x) => !currentIds?.includes(x.id))
            ?.map((x) => ({ value: x.id, label: x?.initialVersion ? `${x?.name} | ${x.initialVersion}` : x?.name }));

        return options?.sort((a, b) => a?.label?.localeCompare(b?.label));
    }, [trialInstitutionItemTemplates, trialInstitutionItemCategories]);

    return (
        <RestrictedLayout
            loading={
                loadingTrialInstitution || 
                loadingTrialInstitutionItemCategories || 
                loadingTrialInstitutionItemTemplates
            }
            permission={Dtos.Permission.TrialInstitutionItemView}
        >
            <div>
                <div className="row">
                    <div className={"col mb-2"}>
                        <h1>Trial Institution Items</h1>
                    </div>
                    <div className="col-auto">
                        {AuthenticationHelper.hasAnyPermission(permissions, [
                            Dtos.Permission.TrialInstitutionItemCreate,
                        ]) && (
                            <>
                                <div className="btn-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        Add Item Template
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                        data-toggle="dropdown"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                    >
                                        <span className="sr-only">
                                            Toggle Dropdown
                                        </span>
                                    </button>
                                    <div className="dropdown-menu dropdown-menu-right">
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trialinstitution/itemtemplates/${trialIdNumber}/template/create`)
                                            }
                                        >
                                            Create Item Template
                                        </a>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() =>
                                                navigate(`/settings/trialinstitution/itemtemplates/${trialIdNumber}`)
                                            }
                                        >
                                            Edit Item Templates
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <a
                                            className="dropdown-item pointer"
                                            onClick={() => setIsOpen(true)}
                                        >
                                            Add Item Template
                                        </a>
                                    </div>
                                </div>
                                <Modal
                                    open={isOpen}
                                    disableCloseOnOverlayClick={true}
                                    onClose={handleModalClose}
                                    size={ModalSize.Lg}
                                >
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            Add Trial Institution Item
                                        </h5>
                                        <button
                                            type="button"
                                            className="close"
                                            aria-label="Close"
                                            onClick={handleModalClose}
                                        >
                                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                                        </button>
                                    </div>
                                    <div className="modal-body p-3">
                                        <div className="form-row pb-2">
                                            <div className="col-12">
                                                {selectOptions?.length === 0 ? (
                                                    <>
                                                        <span>
                                                            No <em>Trial Institution Item Templates</em> are available for <strong>{trialInstitution?.trial?.displayName}</strong> - <strong>{trialInstitution?.institution?.name}</strong>.
                                                        </span>
                                                        <br />
                                                        <button
                                                            className="btn btn-primary mr-1 mt-4"
                                                            onClick={() =>
                                                                navigate(`/settings/trialinstitution/itemtemplates/${trialIdNumber}/template/create`)
                                                            }
                                                        >
                                                            Create Item Template
                                                        </button>
                                                    </>
                                                ) : (
                                                    <SelectFormInput
                                                        loading={loadingTrialInstitutionItemTemplates}
                                                        multi={false}
                                                        options={selectOptions}
                                                        clearable={true}
                                                        backspaceRemoves={false}
                                                        name={"addTrialInstitutionItemId"}
                                                        id={"addTrialInstitutionItemId"}
                                                        placeholder="Trial Institution Item"
                                                        onChange={function (value: any, _options?: any): any {
                                                            setAddTrialInstitutionItemId(
                                                                value != undefined
                                                                    ? parseInt(value)
                                                                    : undefined
                                                            );
                                                        }}
                                                        value={addTrialInstitutionItemId}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-row pt-2">
                                            <div className="col-12 d-flex justify-content-end">
                                                {selectOptions?.length > 0 && (
                                                    <button
                                                        className="btn btn-primary mr-1"
                                                        disabled={
                                                            savingTrialInstitutionItem ||
                                                            selectOptions?.length === 0
                                                        }
                                                        onClick={handleModalSubmit}
                                                    >
                                                        Submit
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-outline-primary ml-1"
                                                    onClick={handleModalClose}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Modal>
                            </>
                        )}
                    </div>
                </div>
                {!showTrialInstitutionItems ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionItems ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                        {renderItemModal}
                        {renderCreateVersionModal}
                    </>
                ) : null}
            </div>
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionItemsPageProps> => {
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let trialInstitutionItemCategories: Dtos.TrialInstitutionItemCategory[] | undefined = (state.trialInstitutionItemCategory.formData instanceof Array)
        ? state.trialInstitutionItemCategory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitution: trialInstitution!,
        loadingTrialInstitution: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        trialInstitutionItemCategories: trialInstitutionItemCategories!,
        loadingTrialInstitutionItemCategories: state.trialInstitutionItemCategory.loadState && state.trialInstitutionItemCategory.loadState.status == RequestState.Pending,
        loadTrialInstitutionItemCategoriesSuccess: state.trialInstitutionItemCategory.loadState && state.trialInstitutionItemCategory.loadState.status == RequestState.Success,
        loadTrialInstitutionItemCategoriesFailure: state.trialInstitutionItemCategory.loadState && state.trialInstitutionItemCategory.loadState.status == RequestState.Failure,
        loadTrialInstitutionItemCategoriesErrorCode: state.trialInstitutionItemCategory.loadState.errorCode,
        permissions: permissions,
        collectionInstanceModalOpen: state.trialInstitutionItemsPage.editModalOpen,
        collectionInstanceId: state.trialInstitutionItemsPage.collectionInstanceId,
        modalTitle: state.trialInstitutionItemsPage.name,
        modalDescription: state.trialInstitutionItemsPage.description,
        trialInstitutionItemTemplates: state.trialInstitutionItemTemplate.formFilteredData!,
        loadingTrialInstitutionItemTemplates: state.trialInstitutionItemTemplate.loadState && state.trialInstitutionItemTemplate.loadState.status == RequestState.Pending,
        loadTrialInstitutionItemTemplatesSuccess: state.trialInstitutionItemTemplate.loadState && state.trialInstitutionItemTemplate.loadState.status == RequestState.Success,
        loadTrialInstitutionItemTemplatesFailure: state.trialInstitutionItemTemplate.loadState && state.trialInstitutionItemTemplate.loadState.status == RequestState.Failure,
        loadTrialInstitutionItemTemplatesErrorCode: state.trialInstitutionItemTemplate.loadState.errorCode,
        savingTrialInstitutionItem: state.trialInstitutionItem.saveState && state.trialInstitutionItem.saveState.status == RequestState.Pending,
        saveTrialInstitutionItemSuccess: state.trialInstitutionItem.saveState && state.trialInstitutionItem.saveState.status == RequestState.Success,
        createVersionModalOpen: state.trialInstitutionItemsPage.createVersionModalOpen,
        createVersionTrialInstitutionItem: state.trialInstitutionItemsPage.trialInstitutionItem,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionItemsPageProps> => {
    return {
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        loadTrialInstitutionItemTemplates: bindActionCreators(TrialInstitutionItemTemplateActions.LoadTrialInstitutionItemTemplate, dispatch),
        clearTrialInstitutionItemTemplates: bindActionCreators(TrialInstitutionItemTemplateActions.Clear, dispatch),
        loadTrialInstitutionItemCategories: bindActionCreators(TrialInstitutionItemCategoryActions.LoadTrialInstitutionItemCategoriesByTrialInstitutionId, dispatch),
        clearTrialInstitutionItemCategories: bindActionCreators(TrialInstitutionItemCategoryActions.Clear, dispatch),
        setEditModelViewState: bindActionCreators(TrialInstitutionItemsPageActions.setEditModalViewState, dispatch),
        setCreateVersionModelViewState: bindActionCreators(TrialInstitutionItemsPageActions.setCreateVersionModalViewState, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        saveTrialInstitutionItem: bindActionCreators(TrialInstitutionItemActions.SaveTrialInstitutionItem, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialInstitutionItemsPage);
